import { Box, Checkbox, MenuItem, Stack, TableCell, TableRow } from "@mui/material";
import { ReactComponent as CheckboxOff } from "assets/images/icons/checkbox-off.svg";
import { ReactComponent as CheckboxOn } from "assets/images/icons/checkbox-on.svg";
import { ReactComponent as MemberIcon } from "assets/images/icons/member.svg";
import { ReactComponent as TrashIcon } from "assets/images/square-buttons/trash.svg";
import { Phrases } from "assets/strings/phrases";
import ConfirmationBox from "components/ConfirmationBox";
import MemberListDropdown from "components/Dropdown/MemberListDropdown";
import ScreenLoader from "components/ScreenLoader";
import Tooltip from "components/Tooltip";
import {
  GENERIC_MEMBER_STATUS_OPTIONS,
  MEMBER_NOT_DELETABLE_ERR_MSG,
  MEMBER_STATUS_ALLOWED_FOR_DELETE,
} from "constants/index";
import { stringToColor } from "helpers/utils";
import moment from "moment";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";

interface MemberType {
  id: any;
  fitlinxx_id: string;
  first_name: string;
  last_name: string;
  last_workout_session: any;
  status: string;
  isChecked: boolean;
  instructor: { id: number; first_name: string; last_name: string };
  instructorOptions: any[];
  onCheckMember: Function;
  onUpdateStatus: Function;
  onUpdateInstructor?: Function;
  onClickDelete?: Function;
  onClickEdit?: Function;
}

export const TableItem = (
  {
    id,
    fitlinxx_id,
    first_name,
    last_name,
    last_workout_session,
    status,
    isChecked,
    instructor,
    instructorOptions,
    onCheckMember,
    onUpdateStatus,
    onUpdateInstructor,
    onClickDelete,
    onClickEdit,
  }: MemberType,
  index
) => {
  const [loading, setLoading] = useState(false);
  const [instructorUpdating, setInstructorUpdating] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [currentInstructor, setCurrentInstructor] = useState(instructor?.id);

  const memberStatusOptions = useMemo(() => {
    return GENERIC_MEMBER_STATUS_OPTIONS.map(
      (e, index) =>
        !e.isSelectAll && (
          <MenuItem
            key={`statusddlentry_${id}_${index}`}
            // selected={e.value === status}
            value={e.value}
          >
            {e.label}
          </MenuItem>
        )
    );
  }, [id]);

  const instructorOptionsToList = useMemo(() => {
    return instructorOptions.map(
      (e, index) =>
        !e.isSelectAll && (
          <MenuItem key={`instructorddlentry_${id}_${index}`} value={e.value}>
            {e.label}
          </MenuItem>
        )
    );
  }, [id]);

  const [confirmationBoxShown, setConfirmationBoxShown] = useState(false);
  const [confirmationBoxMainTitle, setConfirmationBoxMainTitle] = useState("");
  const [confirmationBoxTitle, setConfirmationBoxTitle] = useState("");
  const [confirmationBoxMessage, setConfirmationBoxMessage] = useState(<></>);
  const [confirmationBoxType, setConfirmationBoxType] = useState<"confirmation" | "alert" | "info">(
    "confirmation"
  );
  const [confirmationBoxOnClose, setConfirmationBoxOnClose] = useState(() => void 0);
  const [confirmationBoxIsLoading, setConfirmationBoxIsLoading] = useState(false);

  const showReclaimIdPopup = (id) => {
    setConfirmationBoxMainTitle(Phrases.tcaNewIdNeededTitle);
    setConfirmationBoxTitle(Phrases.tcaNewIdNeededMessage);
    setConfirmationBoxMessage(
      <Box mt={2} mx={2}>
        <ul>
          <li>
            {Phrases.tcaNewIdNeededYes}
            <br />
            <br />
          </li>
          <li>{Phrases.tcaNewIdNeededNo}</li>
        </ul>
      </Box>
    );
    setConfirmationBoxOnClose((_prev, _props) => {
      return (v) => {
        if (typeof v !== "boolean") {
          setConfirmationBoxShown(false);
        }
        if (v) {
          //Positive response
          onClickEdit(id);
          setConfirmationBoxShown(false);
        } else {
          setConfirmationBoxShown(false);
        }
      };
    });
    setConfirmationBoxShown(true);
  };

  return (
    <>
      <TableRow role="checkbox">
        <TableCell sx={{ padding: "0 0 0 20px", width: "38px" }}>
          <Checkbox
            checked={isChecked}
            icon={<CheckboxOff />}
            checkedIcon={<CheckboxOn />}
            onClick={(_e) => onCheckMember(id)}
          />
        </TableCell>
        <TableCell sx={{ padding: 0, width: "43px" }}>
          <Stack direction={"row"} alignItems="center">
            <span
              style={{
                backgroundColor: stringToColor(`${first_name} ${last_name}`),
              }}
              className={"rounded-circle member-icon"}
            >
              <MemberIcon width={24} height={24} stroke={"#fff"} />
            </span>
          </Stack>
        </TableCell>
        <TableCell align="left" className="bold black">
          {last_name}
        </TableCell>
        <TableCell align="left" className="bold black">
          {first_name}
        </TableCell>
        <TableCell align="left" className="bold">
          {fitlinxx_id.length > 5 ? "------" : fitlinxx_id}
        </TableCell>
        <TableCell align="left" className="bold timestamp">
          {last_workout_session && (
            <>
              <h3 className={"grey"}>{moment(last_workout_session).fromNow()}</h3>
              <p className={"grey"}>
                ({moment(last_workout_session).format(`MM-DD-YYYY HH:mm`).toString()})
              </p>
            </>
          )}
        </TableCell>
        <TableCell>
          <span className="status-container">
            {instructorUpdating && <ScreenLoader isScreenLoader />}
            {!instructorUpdating && (
              <>
                <MemberListDropdown
                  value={currentInstructor}
                  defaultValue={currentInstructor ?? ""}
                  onChange={(v) => {
                    setInstructorUpdating(true);
                    onUpdateInstructor(id, v.target.value).then((_e) => {
                      setCurrentInstructor(+v.target.value);
                      setInstructorUpdating(false);
                    });
                  }}
                  fullWidth={true}
                  style={{ marginRight: 10 }}
                  className={`select primary`}
                >
                  {instructorOptionsToList}
                </MemberListDropdown>
              </>
            )}
          </span>
        </TableCell>
        <TableCell>
          <span className="status-container">
            {loading && <ScreenLoader isScreenLoader />}
            {!loading && (
              <MemberListDropdown
                value={currentStatus}
                defaultValue={currentStatus ?? ""}
                onChange={(v) => {
                  if (fitlinxx_id.length > 5 && currentStatus.match("TERMINATED")) {
                    showReclaimIdPopup(id);
                  } else {
                    setLoading(true);
                    onUpdateStatus(id, v.target.value).then((_e) => {
                      setCurrentStatus(v.target.value.toString());
                      setLoading(false);
                    });
                  }
                }}
                fullWidth={true}
                style={{ marginRight: 10 }}
                className={`select primary`}
              >
                {memberStatusOptions}
              </MemberListDropdown>
            )}
          </span>
        </TableCell>
        <TableCell align="right">
          <Box className={"buttons-bar"} width={"auto !important"}>
            <Tooltip
              content={
                ["FITLINXX_TRIAL", "DEMO"].includes(currentStatus)
                  ? "Delete"
                  : "Only trials & demo members can be deleted"
              }
              direction={["FITLINXX_TRIAL", "DEMO"].includes(currentStatus) ? "bottom" : "left"}
            >
              <button
                onClick={() => {
                  if (MEMBER_STATUS_ALLOWED_FOR_DELETE.includes(currentStatus)) {
                    onClickDelete(id);
                  } else {
                    toast(MEMBER_NOT_DELETABLE_ERR_MSG.label, {
                      type: MEMBER_NOT_DELETABLE_ERR_MSG.type,
                    });
                  }
                }}
              >
                <TrashIcon />
              </button>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      <ConfirmationBox
        open={confirmationBoxShown}
        onClose={confirmationBoxOnClose}
        mainTitle={confirmationBoxMainTitle}
        title={confirmationBoxTitle}
        message={confirmationBoxMessage}
        type={confirmationBoxType}
        isLoading={confirmationBoxIsLoading}
      />
    </>
  );
};
